import * as React from "react"
import { BooksZoneType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { validateUserDeletePermission } from "@/lib/api/auth"
import { deleteBook } from "@/lib/api/book"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from "@/components/ui/credenza"
import { Dialog } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import BookForm from "@/components/forms/book-form"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"

interface DataProps {
  data: BooksZoneType
}

export function BooksOperations({ data }: DataProps) {
  const [showDropDown, setShowDropDown] = React.useState<boolean>(false)
  const [showBookZoneModal, setShowBookShowZoneModal] =
    React.useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false)

  return (
    <div>
      <DropdownMenu open={showDropDown} onOpenChange={setShowDropDown}>
        <DropdownMenuTrigger>
          <div className="flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-muted">
            <Icons.ellipsis className="size-4" />
            <span className="sr-only">Open</span>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              setShowBookShowZoneModal(!showBookZoneModal)
              setShowDropDown(false)
            }}
          >
            <Icons.edit className="mr-2 size-4" />
            Edit Books
          </DropdownMenuItem>
          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="text-destructive"
            onSelect={() => {
              setShowDeleteModal(true)
              setShowDropDown(false)
            }}
          >
            <Icons.delete className="mr-2 size-4" />
            Delete Books
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteBookZoneModal
        bookId={data.id}
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
      />

      <EditBookZoneModal
        editData={data}
        showBookZoneModal={showBookZoneModal}
        setShowBookShowZoneModal={setShowBookShowZoneModal}
      />
    </div>
  )
}

function EditBookZoneModal({
  editData,
  showBookZoneModal,
  setShowBookShowZoneModal,
}: {
  editData: BooksZoneType
  showBookZoneModal: boolean
  setShowBookShowZoneModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  return (
    <Credenza open={showBookZoneModal} onOpenChange={setShowBookShowZoneModal}>
      <CredenzaContent className="w-full md:min-w-[600px]">
        <CredenzaTitle>
          <Header>Edit Book Zone</Header>
        </CredenzaTitle>
        <CredenzaBody>
          <BookForm
            editData={editData}
            setShowBookZoneModal={setShowBookShowZoneModal}
          />
        </CredenzaBody>
      </CredenzaContent>
    </Credenza>
  )
}

function DeleteBookZoneModal({
  bookId,
  showModal,
  setShowModal,
}: {
  bookId: number
  showModal: boolean
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const formSchema = z.object({
    id: z.coerce
      .number()
      .min(1, "Please enter the id above to delete")
      .refine((val) => val === bookId, {
        message: "Input ID type must match the Book ID.",
      }),
  })
  const form = useForm({
    resolver: zodResolver(formSchema),
  })

  const mutation = useMutation({
    mutationFn: async () => {
      return await deleteBook(bookId)
    },
    onSuccess: () => {
      toast({
        description: "Book deleted successfully.",
      }),
        queryClient.invalidateQueries({ queryKey: ["books-per-zone"] })
      setShowModal(false)
    },
  })

  function submitDelete() {
    if (!validateUserDeletePermission()) return
    mutation.mutate()
  }
  return (
    <Credenza open={showModal} onOpenChange={setShowModal}>
      <CredenzaContent>
        <Form {...form}>
          <form
            className="space-y-4 pb-4"
            onSubmit={form.handleSubmit(submitDelete)}
          >
            <CredenzaHeader>
              <CredenzaTitle>Delete Book</CredenzaTitle>
              <CredenzaDescription>
                You cannot undo this action!
              </CredenzaDescription>
            </CredenzaHeader>
            <CredenzaBody>
              <FormField
                control={form.control}
                name="id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type "{bookId}" to delete.</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value ?? ""} // Allow empty input
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === ""
                              ? undefined
                              : Number(e.target.value),
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CredenzaBody>
            <CredenzaFooter>
              <CredenzaClose asChild>
                <Button variant={"outline"}>Cancel</Button>
              </CredenzaClose>
              <Button
                disabled={mutation.isPending}
                type="submit"
                variant={"destructive"}
              >
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Delete
              </Button>
            </CredenzaFooter>
          </form>
        </Form>
      </CredenzaContent>
    </Credenza>
  )
}
